/**
 * Admin V3 — Contacts — list component
 *
 * Card-per-message layout (mirrors the legacy V1 Messages tab):
 *   name · email · phone · date, message body, status pill + triage control.
 *
 * Read display is unconditional; the triage control (ContactsStatusControl)
 * HIDE-gates itself on the RBAC permission. Loading = skeleton cards, error =
 * banner + retry, empty = EmptyState.
 */

import React from 'react';
import { Mail, AlertCircle, RotateCcw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { cn, EmptyState, SmallPill } from '../../shared/visualTokens';
import { t } from '../../i18n';
import ContactsStatusControl from './ContactsStatusControl';

// Local status vocabulary → coloured pill (backend: new/read/replied/archived).
const contactStatusMeta = (locale) => ({
  new:      { label: t('contacts.status.new', locale),      pill: 'bg-blue-100 text-blue-700' },
  read:     { label: t('contacts.status.read', locale),     pill: 'bg-slate-100 text-slate-600' },
  replied:  { label: t('contacts.status.replied', locale),  pill: 'bg-emerald-100 text-emerald-700' },
  archived: { label: t('contacts.status.archived', locale), pill: 'bg-slate-100 text-slate-400' },
});

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  } catch {
    return String(iso);
  }
};

const SkeletonCards = () => (
  <div className="space-y-3" data-testid="adminv3-contacts-skeleton">
    {Array(4).fill(0).map((_, i) => (
      <div key={`sk-${i}`} className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="h-3 w-40 rounded bg-slate-100 animate-pulse mb-2" />
        <div className="h-3 w-full max-w-md rounded bg-slate-100 animate-pulse" />
      </div>
    ))}
  </div>
);

const ContactsListTable = ({ contacts, loading, error, isFilterActive, onRetry, onTriaged, locale = 'fr' }) => {
  const meta = contactStatusMeta(locale);

  if (error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="adminv3-contacts-error">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-rose-700">{t('contacts.error.title', locale)}</p>
            <p className="text-xs text-rose-600 mt-1 break-words">{String(error)}</p>
          </div>
          <Button
            variant="outline" size="sm" onClick={onRetry}
            className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
            data-testid="adminv3-contacts-retry"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            {t('contacts.error.retry', locale)}
          </Button>
        </div>
      </div>
    );
  }

  if (loading) return <SkeletonCards />;

  if (!contacts || contacts.length === 0) {
    return (
      <div data-testid="adminv3-contacts-empty">
        <EmptyState
          icon={Mail}
          title={t('contacts.empty.title', locale)}
          desc={t(isFilterActive ? 'contacts.empty.descFilter' : 'contacts.empty.descNoFilter', locale)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="adminv3-contacts-list">
      {contacts.map((c) => {
        const statusMeta = meta[c.status] || meta.new;
        return (
          <div
            key={c.id}
            className={cn('rounded-2xl border border-slate-200 bg-white p-4')}
            data-testid={`adminv3-contacts-row-${c.id}`}
          >
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <div className="font-semibold text-slate-900">{c.name || '—'}</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {[c.email, c.phone].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <SmallPill meta={statusMeta} />
                <span className="text-xs text-slate-400 whitespace-nowrap">{formatDate(c.created_at)}</span>
              </div>
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.message || '—'}</p>
            <div className="mt-3 flex justify-end">
              <ContactsStatusControl contact={c} onTriaged={onTriaged} locale={locale} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ContactsListTable;
